/**
 * useIsMobile — returns true when the viewport is below the Tailwind `md` breakpoint (768px).
 *
 * Mirrors the shadcn `use-mobile` hook. We use it to skip rendering the desktop
 * table (or the mobile card stack) outside its breakpoint so we don't end up
 * with duplicate text nodes that confuse component tests.
 *
 * In JSDOM (component tests) `window.matchMedia` is stubbed to always report
 * `false`, so this hook returns `false` and only the desktop branch renders —
 * preserving the existing test surface area.
 */
import * as React from 'react';

const MOBILE_BREAKPOINT_PX = 768; // Tailwind `md`

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches;
  });

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const onChange = () => setIsMobile(mql.matches);
    // Sync once in case mount value drifted (SSR hydration, viewport resize between render & effect)
    setIsMobile(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}
